import * as React from 'react';
import { BLACKS_AND_WHITES } from '../../utils/DataFormatterUtils';
import { nullSafeGetOrElse, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { Popover } from 'antd';
import {
	getWalkthroughHeatMapColorsForPercentage,
	getWalkthroughHeatMapRangesForWalkthrough,
	parseScorePercentage,
} from '../../utils/WalkthroughHeatMapRangeUtils';
import { isNil } from 'lodash';
// Colors come from the score's own site survey, so a customer that configured custom ranges sees the
// same thresholds here as on its walkthrough heat map. `walkthrough` is optional — without it (or
// on a search-backed row, whose OpenSearch doc does not carry the ranges) this resolves to the
// platform defaults.
const getScoreColors = (scoreObject, walkthrough) => {
	const percentageScore = parseScorePercentage(nullSafeGetWithZero('percentageScore', scoreObject));
	const heatMapRanges = getWalkthroughHeatMapRangesForWalkthrough(walkthrough);
	const colors = getWalkthroughHeatMapColorsForPercentage(percentageScore, heatMapRanges);
	return { statusColor: colors.text, statusBackgroundColor: colors.background };
};

const getScoreMessage = (score, minPossibleScore, maxPossibleScore) =>
	minPossibleScore === 0
		? `Scored ${score} out of ${maxPossibleScore} possible points`
		: `Scored ${score} on a ${minPossibleScore} to ${maxPossibleScore} scale`;

const WalkthroughScoreDisplay = ({
	scoreObject,
	withPrefix = false,
	style = {},
	isPill = false,
	walkthrough = null,
}) => {
	const score = nullSafeGetWithZero('score', scoreObject);
	const maxPossibleScore = nullSafeGetWithZero('maxPossibleScore', scoreObject);
	const minPossibleScore = nullSafeGetOrElse('minPossibleScore', scoreObject, 0);
	const percentageScore = nullSafeGetWithZero('percentageScore', scoreObject);
	const scored = !isNil(score) && !isNil(maxPossibleScore);

	if (!scored && isPill) return null;

	const { statusColor, statusBackgroundColor } = getScoreColors(scoreObject, walkthrough);
	return (
		<div className="flex flex-col">
			<span
				style={{
					...style,
					...(isPill && {
						background: statusBackgroundColor,
						padding: '4px 12px',
						borderRadius: 16,
					}),
				}}
			>
				{scored ? (
					<div className="flex flex-row items-center">
						<Popover
							content={<div>{getScoreMessage(score, minPossibleScore, maxPossibleScore)}</div>}
						>
							<div className="m-0 flex min-w-[10px] flex-1 flex-row justify-center">
								{withPrefix ? <span className="h5regular mr-2 text-[#4B5563]">Score:</span> : null}
								<span className="h5bold" style={{ marginRight: 2, color: statusColor }}>
									{percentageScore}%
								</span>
								<span className="h5bold" style={{ marginRight: 2, color: statusColor }}>
									({score}/{maxPossibleScore})
								</span>
							</div>
						</Popover>
					</div>
				) : null}
			</span>
		</div>
	);
};

export default WalkthroughScoreDisplay;

export const WalkthroughScoreLegend = ({ scoreObject, style = {}, walkthrough = null }) => {
	const score = nullSafeGetWithZero('score', scoreObject);
	const maxPossibleScore = nullSafeGetWithZero('maxPossibleScore', scoreObject);
	const minPossibleScore = nullSafeGetOrElse('minPossibleScore', scoreObject, 0);
	const scored = !isNil(score) && !isNil(maxPossibleScore);

	const { statusColor, statusBackgroundColor } = getScoreColors(scoreObject, walkthrough);
	return (
		<span
			style={{
				color: statusColor,
				...style,
			}}
		>
			{scored ? (
				<div className="flex flex-row items-center">
					<Popover
						content={<div>{getScoreMessage(score, minPossibleScore, maxPossibleScore)}</div>}
					>
						<div className="m-0 min-w-[10px]">
							<div
								className={`h-4 w-4 rounded transition-transform duration-100 hover:scale-105 `}
								style={{
									backgroundColor: statusBackgroundColor,
								}}
							/>
						</div>
					</Popover>
				</div>
			) : (
				<span style={{ color: BLACKS_AND_WHITES.blackSecondary }}>-/-</span>
			)}
		</span>
	);
};
